import type { ShiftKind, MyShift } from '@keetcheck/shared';

export const KIND_LABEL: Record<ShiftKind, string> = {
  duty_crew: 'Duty crew',
  udo: 'UDO',
  udo_day: 'Day UDO',
  ops_support: 'Ops support',
  activity: 'Activity',
};

export const AS_LABEL: Record<MyShift['as'], string> = {
  duty_crew: 'Duty crew',
  udo: 'UDO',
  udo_day: 'Day UDO',
  ops_support: 'Ops support',
  ops_team: 'Ops support (your team)',
  cover: 'Covering',
  activity: 'Activity',
};

/** "Sat 10 Oct 6pm–6am", or "Fri 9 Oct 6pm – Mon 12 Oct 6am" for longer ranges. */
export function rangeLabel(startsAt: string, endsAt: string): string {
  const a = new Date(startsAt), b = new Date(endsAt);
  return b.getTime() - a.getTime() <= 24 * 3600_000
    ? `${dayLabel(startsAt)} ${timeRange(startsAt, endsAt)}`
    : `${dayLabel(startsAt)} ${time(a)} – ${dayLabel(endsAt)} ${time(b)}`;
}

/**
 * The moment within [from, to] that shows `hhmm` on the local clock (first
 * match) — for picking part of a shift that crosses midnight.
 */
export function clockWithin(hhmm: string, from: string, to: string): string | null {
  const [h, m] = hhmm.split(':').map(Number);
  const start = new Date(from), end = new Date(to);
  for (let d = 0; d <= 14; d++) {
    const c = new Date(start);
    c.setDate(c.getDate() + d);
    c.setHours(h!, m!, 0, 0);
    if (c >= start && c <= end) return c.toISOString();
    if (c > end) break;
  }
  return null;
}

/** "18:00" for an ISO time (for <input type="time">). */
export function hhmm(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** "6pm", "6:30pm" — or "18:00" on a phone set to 24-hour time, where "18" alone reads oddly. */
export const time = (d: Date) => {
  const s = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return /[ap]\.?\s?m/i.test(s) ? s.replace(':00', '').replace(/[\s.]/g, '').toLowerCase() : s;
};

/** "6pm – 6am" */
export function timeRange(startsAt: string, endsAt: string): string {
  return `${time(new Date(startsAt))} – ${time(new Date(endsAt))}`;
}

/** "Mon 9 Nov" */
export function dayLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}

/** Local calendar day of a shift's start, YYYY-MM-DD (for grouping). */
export function localDay(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Day shift (starts in the morning) vs night shift. */
export const isDayShift = (iso: string) => new Date(iso).getHours() < 12;

export function addDays(ymd: string, n: number): string {
  const d = new Date(`${ymd}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** "9 Nov" for a YYYY-MM-DD */
export function shortDate(ymd: string): string {
  return new Date(`${ymd}T00:00:00Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

/** Monday of the current duty week (before Monday 6pm it's still last week). */
export function currentDutyMonday(now = new Date()): string {
  const d = new Date(now);
  const back = (d.getDay() + 6) % 7 + (d.getDay() === 1 && d.getHours() < 18 ? 7 : 0);
  d.setDate(d.getDate() - back);
  return localDay(d.toISOString());
}
